import {
  Box,
  Button,
  HStack,
  Portal,
  Select,
  Text,
  SegmentGroup,
  VStack,
  Code,
  Spinner,
  Textarea,
} from "@chakra-ui/react";
import { LuCodeXml, LuTable, LuFileOutput } from "react-icons/lu";
import { useState, useEffect, useCallback } from "react";
import {
  USER_DEFINED_SCHEMA_ID,
  convertToSimpleSchema,
  type SchemaField,
} from "@/components/Document/DocumentSchema";
import DeleteSchema from "@/components/Schema/DeleteSchema";
import EditSchema from "@/components/Schema/EditSchema";
import { useSchemaContext } from "@/context/SchemaContext";

// 표준화된 추출 결과 타입 정의
interface StandardizedExtractionResult {
  success: boolean;
  pages: number;
  data: Record<string, unknown>;
  fileName: string;
}

// 타입 가드 함수
function isStandardizedResult(
  result: unknown
): result is StandardizedExtractionResult {
  return (
    result !== null &&
    typeof result === "object" &&
    "success" in result &&
    "pages" in result &&
    "data" in result &&
    "fileName" in result
  );
}

interface SchemaManagementProps {
  onSaveClick: () => void;
  onEditSuccess: () => void;
  onDeleteSuccess: () => void;
  currentSchema?: SchemaField[]; // 현재 스키마 데이터
  isExtracting?: boolean;
  extractedResult?: unknown;
  onDataExtraction?: () => void;
  onSchemaChange?: (schema: SchemaField[]) => void;
}

const SchemaManagement = ({
  onSaveClick,
  onEditSuccess,
  onDeleteSuccess,
  currentSchema,
  isExtracting = false,
  extractedResult,
}: SchemaManagementProps) => {
  const {
    selectedTemplate,
    viewMode,
    setViewMode,
    dbSchemas,
    dynamicChakraSchemaList,
    handleTemplateChange,
    fetchSchemas,
    convertDBSchemaToSchemaPublic,
  } = useSchemaContext();

  // 현재 스키마를 JSON으로 변환 (초기값 제외)
  const schemaJson = currentSchema ? convertToSimpleSchema(currentSchema) : [];

  // 편집 가능한 추출 결과 상태
  const [editableResult, setEditableResult] = useState<string>("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  // extractedResult가 변경될 때 편집 가능한 텍스트 업데이트
  useEffect(() => {
    if (extractedResult) {
      try {
        if (isStandardizedResult(extractedResult)) {
          setEditableResult(JSON.stringify(extractedResult.data, null, 2));
        } else {
          setEditableResult(JSON.stringify(extractedResult, null, 2));
        }
        setJsonError(null);
      } catch {
        setEditableResult(String(extractedResult));
        setJsonError("JSON 파싱 오류");
      }
    } else {
      setEditableResult("");
      setJsonError(null);
    }
  }, [extractedResult]);

  // JSON 유효성 검사 및 포맷팅
  const handleResultChange = useCallback((value: string) => {
    setEditableResult(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch {
      setJsonError("유효하지 않은 JSON 형식입니다.");
    }
  }, []);

  // JSON 포맷팅
  const handleFormatJson = useCallback(() => {
    try {
      const parsed = JSON.parse(editableResult);
      setEditableResult(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch {
      setJsonError("JSON 포맷팅 실패: 유효하지 않은 JSON 형식입니다.");
    }
  }, [editableResult]);

  // 템플릿 변경 시 스키마 로드
  const handleTemplateChangeWithSchemaLoad = (templateName: string) => {
    // Context의 handleTemplateChange만 호출 (스키마 로드는 DocumentSchema에서 처리)
    handleTemplateChange(templateName);
  };

  // 스키마 삭제 성공 시 처리
  const handleDeleteSuccess = () => {
    // 스키마 목록 다시 가져오기
    fetchSchemas();
    // 선택된 템플릿을 사용자 정의로 초기화
    handleTemplateChangeWithSchemaLoad(USER_DEFINED_SCHEMA_ID);
    // 부모 컴포넌트의 onDeleteSuccess 호출
    onDeleteSuccess();
  };

  return (
    <VStack align="stretch" gap={4}>
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={2}>
          스키마 도구
        </Text>
        <Text fontSize="sm" mb={2}>
          스키마를 설정하고 데이터를 추출할 수 있습니다.
        </Text>

        <HStack gap={2} mb={4}>
          <Box
            p={0}
            borderRadius="md"
            display="flex"
            justifyContent="center"
            alignItems="center"
            w="320px"
            gap={2}
          >
            <Select.Root
              collection={dynamicChakraSchemaList}
              size="sm"
              width="320px"
              value={[selectedTemplate]}
              onValueChange={(details) => {
                const templateName = details.value[0];
                if (templateName && templateName !== selectedTemplate) {
                  handleTemplateChangeWithSchemaLoad(templateName);
                }
              }}
            >
              <Select.HiddenSelect />
              <Select.Control>
                <Select.Trigger>
                  <Select.ValueText placeholder="스키마 선택" />
                </Select.Trigger>
                <Select.IndicatorGroup>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {dynamicChakraSchemaList.items.map((s) => (
                      <Select.Item
                        item={s}
                        key={(s as { value: string }).value}
                      >
                        {(s as { label: string }).label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>

            <SegmentGroup.Root
              value={viewMode}
              onValueChange={(details) =>
                setViewMode(details.value as "table" | "code" | "result")
              }
              size="sm"
            >
              <SegmentGroup.Indicator />
              <SegmentGroup.Items
                items={[
                  { value: "table", label: <LuTable size={16} /> },
                  { value: "code", label: <LuCodeXml size={16} /> },
                  { value: "result", label: <LuFileOutput size={16} /> },
                ]}
              />
            </SegmentGroup.Root>
          </Box>
          {selectedTemplate === USER_DEFINED_SCHEMA_ID ? (
            <Button size="sm" onClick={onSaveClick}>
              저장
            </Button>
          ) : dbSchemas.some((s) => s.id === selectedTemplate) &&
            viewMode === "table" ? (
            <HStack gap={2}>
              {(() => {
                const selectedSchema = dbSchemas.find(
                  (s) => s.id === selectedTemplate
                );

                if (selectedSchema) {
                  return (
                    <>
                      <EditSchema
                        schema={convertDBSchemaToSchemaPublic(selectedSchema)}
                        currentSchemaContent={convertToSimpleSchema(
                          currentSchema || []
                        )}
                        onSuccess={onEditSuccess}
                      />
                      <DeleteSchema
                        id={selectedTemplate}
                        onSuccess={handleDeleteSuccess}
                      />
                    </>
                  );
                }

                return null;
              })()}
            </HStack>
          ) : null}
        </HStack>
      </Box>

      {/* JSON 뷰 */}
      {viewMode === "code" && (
        <Box
          borderWidth={1}
          borderRadius="md"
          p={4}
          bg="gray.50"
          h="600px"
          overflowY="auto"
        >
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            스키마 JSON 구조
          </Text>
          <Code
            display="block"
            whiteSpace="pre-wrap"
            fontSize="xs"
            p={3}
            borderRadius="md"
            bg="white"
            borderWidth={1}
            borderColor="gray.200"
            maxH="500px"
            overflowY="auto"
          >
            {JSON.stringify(schemaJson, null, 2)}
          </Code>
        </Box>
      )}

      {/* 추출 결과 뷰 */}
      {viewMode === "result" && (
        <Box
          borderWidth={1}
          borderRadius="md"
          p={4}
          bg="gray.50"
          h="600px"
          overflowY="auto"
        >
          <Text fontSize="sm" fontWeight="bold" mb={2}>
            추출 결과
          </Text>

          {isExtracting ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={12}
              bg="white"
              borderRadius="md"
              borderWidth={1}
              borderColor="gray.200"
            >
              <VStack gap={4}>
                <Spinner size="xl" color="blue.500" />
                <VStack gap={1}>
                  <Text fontSize="md" fontWeight="semibold" color="gray.700">
                    데이터 추출 중...
                  </Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    문서를 분석하고 스키마에 따라 데이터를 추출하고 있습니다.
                    <br />
                    잠시만 기다려주세요.
                  </Text>
                </VStack>
              </VStack>
            </Box>
          ) : extractedResult ? (
            <Box>
              <HStack justify="space-between" mb={3}>
                <Text fontSize="sm" fontWeight="semibold" color="green.600">
                  ✅ 추출 완료
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {new Date().toLocaleString()}
                </Text>
              </HStack>

              {/* 표준화된 응답 형식 처리 */}
              {isStandardizedResult(extractedResult) ? (
                <VStack align="stretch" gap={3}>
                  <HStack
                    justify="space-between"
                    fontSize="xs"
                    color="gray.600"
                  >
                    <Text>파일: {extractedResult.fileName}</Text>
                    <Text>페이지: {extractedResult.pages}페이지</Text>
                  </HStack>
                  <VStack align="stretch" gap={2}>
                    <HStack justify="flex-end">
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={handleFormatJson}
                      >
                        JSON 포맷팅
                      </Button>
                    </HStack>
                    <Textarea
                      value={editableResult}
                      onChange={(e) => handleResultChange(e.target.value)}
                      fontFamily="mono"
                      fontSize="xs"
                      p={3}
                      borderRadius="md"
                      bg="white"
                      borderWidth={1}
                      borderColor={jsonError ? "red.300" : "gray.200"}
                      maxH="800px"
                      minH="600px"
                      resize="vertical"
                      _focus={{
                        borderColor: jsonError ? "red.500" : "blue.500",
                        boxShadow: jsonError
                          ? "0 0 0 1px var(--chakra-colors-red-500)"
                          : "0 0 0 1px var(--chakra-colors-blue-500)",
                      }}
                    />
                    {jsonError && (
                      <Text fontSize="xs" color="red.500">
                        {jsonError}
                      </Text>
                    )}
                  </VStack>
                </VStack>
              ) : (
                <VStack align="stretch" gap={2}>
                  <HStack justify="flex-end">
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={handleFormatJson}
                    >
                      JSON 포맷팅
                    </Button>
                  </HStack>
                  <Textarea
                    value={editableResult}
                    onChange={(e) => handleResultChange(e.target.value)}
                    fontFamily="mono"
                    fontSize="xs"
                    p={3}
                    borderRadius="md"
                    bg="white"
                    borderWidth={1}
                    borderColor={jsonError ? "red.300" : "gray.200"}
                    maxH="800px"
                    minH="600px"
                    resize="vertical"
                    _focus={{
                      borderColor: jsonError ? "red.500" : "blue.500",
                      boxShadow: jsonError
                        ? "0 0 0 1px var(--chakra-colors-red-500)"
                        : "0 0 0 1px var(--chakra-colors-blue-500)",
                    }}
                  />
                  {jsonError && (
                    <Text fontSize="xs" color="red.500">
                      {jsonError}
                    </Text>
                  )}
                </VStack>
              )}
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              py={8}
              bg="white"
              borderRadius="md"
              borderWidth={1}
              borderColor="gray.200"
            >
              <VStack gap={2}>
                <LuFileOutput size={32} color="gray.400" />
                <Text fontSize="sm" color="gray.600">
                  추출된 데이터가 없습니다.
                </Text>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  테이블 뷰에서 "데이터 추출" 버튼을 클릭하여 문서에서 데이터를
                  추출하세요.
                </Text>
              </VStack>
            </Box>
          )}
        </Box>
      )}
    </VStack>
  );
};

export default SchemaManagement;
